"use client"

import * as React from "react"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuCheckboxItem,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuLabel,
} from "../../components/context-menu"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import type { LucideIcon } from "lucide-react"

// ============================================================================
// UIContextMenu Types
// ============================================================================

export interface UIContextMenuItemBase {
  id?: string
  label: string
  disabled?: boolean
  icon?: LucideIcon
}

export interface UIContextMenuActionItem extends UIContextMenuItemBase {
  type: "item"
  onClick?: () => void
  shortcut?: string
  destructive?: boolean
}

export interface UIContextMenuCheckboxItemData extends UIContextMenuItemBase {
  type: "checkbox"
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  shortcut?: string
}

export interface UIContextMenuRadioItemData extends UIContextMenuItemBase {
  type: "radio"
  value: string
  shortcut?: string
}

export interface UIContextMenuRadioGroupData {
  type: "radio-group"
  id?: string
  label?: string
  value?: string
  onValueChange?: (value: string) => void
  items: UIContextMenuRadioItemData[]
}

export interface UIContextMenuSeparatorData {
  type: "separator"
  id?: string
}

export interface UIContextMenuLabelData {
  type: "label"
  id?: string
  label: string
}

export interface UIContextMenuSubmenuData extends UIContextMenuItemBase {
  type: "submenu"
  items: UIContextMenuItemData[]
}

export type UIContextMenuItemData =
  | UIContextMenuActionItem
  | UIContextMenuCheckboxItemData
  | UIContextMenuRadioGroupData
  | UIContextMenuSeparatorData
  | UIContextMenuLabelData
  | UIContextMenuSubmenuData

const contextMenuVariants = cva(
  "w-full max-w-full min-w-0 flex-shrink-0", // Width Stability 패턴
  {
    variants: {
      variant: {
        default: "bg-popover border-border",
        elevated: "bg-popover border-border shadow-lg",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface UIContextMenuProps extends
  React.ComponentProps<typeof ContextMenu>,
  VariantProps<typeof contextMenuVariants> {
  /**
   * 컨텍스트 메뉴가 적용될 대상 요소
   */
  children: React.ReactNode
  /**
   * 컨텍스트 메뉴 아이템들
   */
  items: UIContextMenuItemData[]
  /**
   * 메뉴가 열릴 때 호출되는 콜백
   */
  onOpenChange?: (open: boolean) => void
  /**
   * 컨테이너 클래스명
   */
  className?: string
  /**
   * 컨텍스트 메뉴 비활성화 여부
   */
  disabled?: boolean
}

// ============================================================================
// UIContextMenu Component
// ============================================================================

export const UIContextMenu = React.forwardRef<
  React.ComponentRef<typeof ContextMenu>,
  UIContextMenuProps
>((
  {
    children,
    items,
    variant,
    onOpenChange,
    className,
    disabled = false,
    ...props
  },
  ref
) => {
  const renderMenuItem = (item: UIContextMenuItemData, index: number): React.ReactNode => {
    const key = item.id || `item-${index}`

    switch (item.type) {
      case "item":
        return (
          <ContextMenuItem
            key={key}
            disabled={item.disabled}
            onClick={item.onClick}
            variant={item.destructive ? "destructive" : "default"}
            className={cn(
              item.icon && "flex items-center gap-2"
            )}
          >
            {item.icon && <item.icon className="h-4 w-4" />}
            {item.label}
            {item.shortcut && <ContextMenuShortcut>{item.shortcut}</ContextMenuShortcut>}
          </ContextMenuItem>
        )

      case "checkbox":
        return (
          <ContextMenuCheckboxItem
            key={key}
            disabled={item.disabled}
            checked={item.checked}
            onCheckedChange={item.onCheckedChange}
          >
            {item.icon && <item.icon className="h-4 w-4" />}
            {item.label}
            {item.shortcut && <ContextMenuShortcut>{item.shortcut}</ContextMenuShortcut>}
          </ContextMenuCheckboxItem>
        )

      case "radio-group":
        return (
          <ContextMenuRadioGroup
            key={key}
            value={item.value}
            onValueChange={item.onValueChange}
          >
            {item.label && <ContextMenuLabel>{item.label}</ContextMenuLabel>}
            {item.items.map((radioItem, radioIndex) => (
              <ContextMenuRadioItem
                key={radioItem.id || `radio-${radioIndex}`}
                value={radioItem.value}
                disabled={radioItem.disabled}
              >
                {radioItem.icon && <radioItem.icon className="h-4 w-4" />}
                {radioItem.label}
                {radioItem.shortcut && <ContextMenuShortcut>{radioItem.shortcut}</ContextMenuShortcut>}
              </ContextMenuRadioItem>
            ))}
          </ContextMenuRadioGroup>
        )

      case "separator":
        return <ContextMenuSeparator key={key} />

      case "label":
        return <ContextMenuLabel key={key}>{item.label}</ContextMenuLabel>

      case "submenu":
        return (
          <ContextMenuSub key={key}>
            <ContextMenuSubTrigger disabled={item.disabled}>
              {item.icon && <item.icon className="h-4 w-4 mr-2" />}
              {item.label}
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {item.items.map((subItem, subIndex) => renderMenuItem(subItem, subIndex))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )

      default:
        return null
    }
  }

  if (disabled) {
    return <>{children}</>
  }

  return (
    <ContextMenu onOpenChange={onOpenChange} {...props}>
      <ContextMenuTrigger asChild>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className={cn(contextMenuVariants({ variant }), className)}>
        {items.map((item, index) => renderMenuItem(item, index))}
      </ContextMenuContent>
    </ContextMenu>
  )
})

UIContextMenu.displayName = "UIContextMenu"

// ============================================================================
// Preset Context Menu Components
// ============================================================================

export interface UIContextMenuSimpleProps extends Omit<UIContextMenuProps, 'items'> {
  items: UIContextMenuItemData[]
}

export const UIContextMenuSimple = React.forwardRef<
  React.ComponentRef<typeof ContextMenu>,
  UIContextMenuSimpleProps
>((props, ref) => {
  return <UIContextMenu ref={ref} {...props} />
})

UIContextMenuSimple.displayName = "UIContextMenuSimple"

// ============================================================================
// Business Logic Presets
// ============================================================================

export interface UIContextMenuEditableProps extends Omit<UIContextMenuProps, 'items'> {
  onEdit?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  onShare?: () => void
  editLabel?: string
  duplicateLabel?: string
  deleteLabel?: string
  shareLabel?: string
  showEdit?: boolean
  showDuplicate?: boolean
  showDelete?: boolean
  showShare?: boolean
}

/**
 * 편집 가능한 아이템을 위한 사전 정의된 컨텍스트 메뉴
 * 일반적인 CRUD 작업을 포함합니다.
 */
export const UIContextMenuEditable = React.forwardRef<
  React.ComponentRef<typeof ContextMenu>,
  UIContextMenuEditableProps
>((
  {
    onEdit,
    onDuplicate,
    onDelete,
    onShare,
    editLabel = "편집",
    duplicateLabel = "복제",
    deleteLabel = "삭제",
    shareLabel = "공유",
    showEdit = true,
    showDuplicate = false,
    showDelete = true,
    showShare = false,
    ...props
  },
  ref
) => {
  const items: UIContextMenuItemData[] = []

  if (showEdit && onEdit) {
    items.push({
      type: "item",
      label: editLabel,
      onClick: onEdit,
      shortcut: "⌘E",
    })
  }

  if (showDuplicate && onDuplicate) {
    items.push({
      type: "item",
      label: duplicateLabel,
      onClick: onDuplicate,
      shortcut: "⌘D",
    })
  }

  if (showShare && onShare) {
    items.push({
      type: "item",
      label: shareLabel,
      onClick: onShare,
      shortcut: "⌘S",
    })
  }

  // Add separator before destructive action
  if (showDelete && items.length > 0) {
    items.push({ type: "separator" })
  }

  if (showDelete && onDelete) {
    items.push({
      type: "item",
      label: deleteLabel,
      onClick: onDelete,
      destructive: true,
      shortcut: "⌘⌫",
    })
  }

  return <UIContextMenu ref={ref} items={items} {...props} />
})

UIContextMenuEditable.displayName = "UIContextMenuEditable"

// ============================================================================
// Individual Component Exports
// ============================================================================

export const UIContextMenuTrigger = ContextMenuTrigger
export const UIContextMenuContent = ContextMenuContent
export const UIContextMenuItem = ContextMenuItem
export const UIContextMenuSeparator = ContextMenuSeparator
export const UIContextMenuShortcut = ContextMenuShortcut

// ============================================================================
// Type Re-exports
// ============================================================================
// Note: All types are automatically exported via their interface declarations